// Lib
import { captureException } from '@sentry/browser';
import { put, select, takeLatest } from 'redux-saga/effects';

// Api
import client from '@api';

// Services
import {
  getActiveSessionToken,
  setActiveSessionToken,
} from '@services/sessionToken';

// Actions
import { UserError } from '@actions/user';
import { AccountDataLoad, UpdateAccount } from '@actions/account';
import { SelectOrganization } from '@actions/organization';
import { SetLoading } from '@actions/loading';
import { SetRedirectControl } from '@actions/redirectControl';

// Graph
import { getUserDataQuery } from '@graphql/queries/data';

// Types
import type { RootState, SelectedOrganizationState } from '@app-types/index';

interface Organization {
  _id: string;
  type: string;
  token?: string;
  [key: string]: unknown;
}

interface Account {
  _id: string;
  organizations?: Organization[];
  [key: string]: unknown;
}

interface AccountDataResponse {
  data: {
    account: Account;
  };
}

export function* accountDataLoadSaga(): Generator<unknown, void, unknown> {
  yield put(SetLoading(true));

  try {
    const response = (yield client().query({
      query: getUserDataQuery,
      fetchPolicy: 'no-cache',
      variables: { input: { skip: 0 } },
    })) as AccountDataResponse;

    const account = response.data.account;

    // Stale-response guard. GetAccountData is `no-cache`, so a load started for
    // one session can still be in flight across a logout / account-switch.
    // `takeLatest` only cancels when a *new* AccountDataLoad is dispatched — a
    // logout dispatches none, so without this gate an in-flight response could
    // `UpdateAccount` (and set a session token) for an account the user has
    // already left.
    //
    // - Logout clears the session JWT from storage (sessionSagas.logout), so a
    //   missing token means the user is no longer authenticated; drop the
    //   result. This stays correct for the magic-link initial load, where
    //   useLogin writes the token *before* dispatching AccountDataLoad and the
    //   store `_id` is still empty.
    // - An account switch is detected by comparing the response account against
    //   the live store `_id` (only when the store already has one).
    const hasSession = !!getActiveSessionToken();
    const currentAccountId = (yield select(
      (state: RootState) => state.Account?._id,
    )) as string | null;
    const accountSwitched =
      !!currentAccountId && !!account?._id && currentAccountId !== account._id;

    if (!hasSession || accountSwitched) {
      return;
    }

    yield put(UpdateAccount(account));

    // Read SelectedOrganization *after* the request resolves, not before: the
    // user may have switched org while GetAccountData was in flight (e.g. via
    // the account menu, which updates the active org token immediately). Using
    // the live selection here avoids re-applying a stale org's token.
    const selectedOrganization = (yield select(
      (state: RootState) => state.SelectedOrganization,
    )) as SelectedOrganizationState | null;

    if (selectedOrganization) {
      // Re-sync the selected org from the freshly-fetched account, don't just
      // read its token. SelectedOrganization is in the redux-persist whitelist,
      // so its fields are restored from localStorage on boot — including the
      // org avatar `url`, a short-lived signed CloudFront link the resolver
      // mints per request. Without re-selecting, the AccountMenu button (which
      // reads SelectedOrganization) renders an expired (403) avatar captured at
      // a past load, while Account.organizations — refreshed every boot — shows
      // a fresh one. Re-dispatching keeps the whole slice current. (#81)
      const freshOrg = account?.organizations?.find(
        ({ _id }: Organization) => _id === selectedOrganization._id,
      );

      if (freshOrg) {
        yield put(SelectOrganization(freshOrg));

        if (freshOrg.token) {
          setActiveSessionToken(freshOrg.token);
        }
      } else if (account?.organizations?.length) {
        // The selected org is gone from the account (user left / access
        // revoked). Re-home onto the first available org so we don't boot into
        // a phantom selection pointing at an org we can no longer access, with
        // a now-invalid token. No redirect — the user keeps their current view;
        // org-specific routes are handled by their own guards. (#82)
        const fallbackOrg = account.organizations[0];
        yield put(SelectOrganization(fallbackOrg));

        if (fallbackOrg.token) {
          setActiveSessionToken(fallbackOrg.token);
        }
      } else {
        // The account has NO organizations left (removed from the last one).
        // There's nothing to re-home onto, so clear the now-phantom selection
        // instead of booting with a dead org pinned. Token/routing for a
        // zero-org account belong to the no-org / onboarding flow, not here;
        // the account menu is already hidden while orgs are empty. (#82)
        yield put(SelectOrganization(null));
      }
    } else {
      if (account?.organizations && account?.organizations?.length) {
        yield put(SelectOrganization(account?.organizations?.[0]));

        if (account?.organizations?.[0]?.token) {
          setActiveSessionToken(account.organizations[0].token);
        }

        yield put(SetRedirectControl('/home'));
      }
    }
  } catch (error) {
    captureException(error);
    yield put(UserError(error));
  } finally {
    yield put(SetLoading(false));
  }
}

function* dataSaga(): Generator<unknown, void, unknown> {
  // `takeLatest` (not `takeEvery`): GetAccountData is a `no-cache` network
  // query, so every dispatch is a guaranteed round-trip. During the auth →
  // org-select → first-navigation cascade several `AccountDataLoad` actions
  // could fire in a tight burst — with `takeEvery` each spawned its own
  // concurrent fetch (a source of the 15+ refetches) and racing responses
  // could apply stale account/token data out of order. `takeLatest` cancels
  // any in-flight load when a new one arrives, so only the most recent request
  // applies its result. This deduplicates overlapping loads while guaranteeing
  // a genuinely-new login/account-switch always loads fresh data (the latest
  // dispatch wins) and never gets dropped — which `takeLeading` could do.
  yield takeLatest(AccountDataLoad, accountDataLoadSaga);
}

export default dataSaga;
